import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import config from '@/config/config.env'
import { supabase } from '@/services/supabase'

// ─── W3C Traceparent Generator ───────────────────────────────────────────────
function generateTraceparent(): string {
  const version = '00'
  const traceId = Array.from({ length: 32 }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join('')
  const parentId = Array.from({ length: 16 }, () =>
    Math.floor(Math.random() * 16).toString(16)
  ).join('')
  const flags = '01'
  return `${version}-${traceId}-${parentId}-${flags}`
}

interface TracedAxiosRequestConfig extends InternalAxiosRequestConfig {
  traceparent?: string
  _retry?: boolean
}

// ─── Module-Level Token Cache ────────────────────────────────────────────────
// Updated by AuthProvider's onAuthStateChange. Avoids supabase.auth.getSession()
// contention on every API call (which was adding 10-50ms per request).
let cachedAuthToken: string | null = null

export function setApiAuthToken(token: string | null) {
  cachedAuthToken = token
}

// ─── INDUSTRY-BEST: Single-Flight Token Refresh Queue ────────────────────────
// When multiple API calls return 401 simultaneously, only ONE refresh is triggered.
// All other 401-failed requests wait for that single refresh to complete, then retry
// with the new token. This prevents the "refresh token race" where multiple concurrent
// refreshes invalidate each other's tokens (the root cause of cascade logouts).
// This is the pattern used by Linear, Vercel, and Stripe Dashboard.
let refreshPromise: Promise<string | null> | null = null

async function refreshTokenOnce(): Promise<string | null> {
  // If a refresh is already in-flight, return the same promise.
  // This is the "single-flight" guarantee — no duplicate refreshes.
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const { data, error } = await supabase.auth.refreshSession()
      if (error || !data.session) {
        console.warn('[API] Token refresh failed:', error?.message)
        return null
      }
      const newToken = data.session.access_token
      setApiAuthToken(newToken)
      return newToken
    } catch (err) {
      console.warn('[API] Token refresh exception:', err)
      return null
    } finally {
      // Clear the promise so the next 401 can trigger a fresh refresh
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// ─── Axios Instance ──────────────────────────────────────────────────────────
const api = axios.create({
  baseURL: config.urls.baseUrl,
})

// ─── Request Interceptor ─────────────────────────────────────────────────────
// Attaches the cached auth token and W3C traceparent header.
// REMOVED: the getSession() fallback that was adding 10-50ms per request.
// The token is always set by AuthProvider's onAuthStateChange before any API
// calls are made, so the fallback was unnecessary and caused contention.
api.interceptors.request.use(
  async (config: TracedAxiosRequestConfig) => {
    // W3C traceparent for distributed tracing
    const traceparent = generateTraceparent()
    config.headers['traceparent'] = traceparent
    config.traceparent = traceparent

    // Use cached token directly — or retrieve from Supabase session if cache is not yet warmed
    if (cachedAuthToken) {
      config.headers.Authorization = `Bearer ${cachedAuthToken}`
    } else {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (session?.access_token) {
          cachedAuthToken = session.access_token
          config.headers.Authorization = `Bearer ${session.access_token}`
        }
      } catch (_) {}
    }
    return config
  },
  (error) => Promise.reject(error)
)

// ─── Response Interceptor ────────────────────────────────────────────────────
// Handles 401 responses with the single-flight refresh queue.
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as TracedAxiosRequestConfig

    // Extract correlation ID for error tracking
    let correlationId = 'N/A'
    if (error.response?.data && typeof error.response.data === 'object') {
      const body = error.response.data as Record<string, any>
      correlationId = body.error?.correlation_id || correlationId
    }
    if (correlationId === 'N/A' && originalRequest?.traceparent) {
      const parts = originalRequest.traceparent.split('-')
      if (parts.length === 4) correlationId = parts[1]
    }
    ;(error as any).correlationId = correlationId

    if (import.meta.env.MODE === 'development') {
      console.error(
        `[API ERROR] ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url} | Status: ${error.response?.status} | Trace: ${correlationId}`
      )
    }

    // ─── 401 Handling: Single-Flight Refresh ───────────────────────────
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true

      const newToken = await refreshTokenOnce()
      if (newToken) {
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return api(originalRequest)
      }

      // Refresh failed — token is truly expired.
      // Don't hard-redirect here. Let the AuthProvider's onAuthStateChange
      // handle the SIGNED_OUT event and redirect gracefully.
      // This prevents the jarring `window.location.href = /login` redirect
      // that was previously causing flash-of-content issues.
      cachedAuthToken = null
    }

    return Promise.reject(error)
  }
)

export default api
